/**
	* Map tool mode tools
	* Handles: description
	* @module tools/toolmodes
*/

import MapToolMode from "lib/enums/maptoolmode";
import FeatureMode from "lib/enums/featuremode";
import { getIndexByValue } from "lib/utilities/misc";
import { getLocaleString } from "lib/localization/vendor";

export const toolModeNames:Map<MapToolMode, string> = new Map<MapToolMode, string>([
	[MapToolMode.Select, "selecttool"],
	[MapToolMode.Draw, "drawtool"],
	[MapToolMode.Address, "addresstool"],
	[MapToolMode.Upload, "uploadtool"],
	[MapToolMode.Print, "printtool"],
	[MapToolMode.Measure, "measuretool"],
	[MapToolMode.MapLayers, "maplayers"],
	[MapToolMode.Locations, "locations"],
	[MapToolMode.Filter, "filtertool"],
	[MapToolMode.Tutorial, "tutorial"],
	[MapToolMode.Permalink, "permalink"],
	[MapToolMode.Annotation, "annotation"],
	[MapToolMode.MapControl, "mapcontrol"],
	[MapToolMode.LinkedResources, "linkedresources"],
	[MapToolMode.Accessibility, "accessibility"]
]);

export const toolKeybinds:Map<MapToolMode, string> = new Map<MapToolMode, string>([
	[MapToolMode.Select, "v"],
	[MapToolMode.Draw, "d"],
	[MapToolMode.Address, "a"],
	[MapToolMode.Upload, "u"],
	[MapToolMode.Print, "p"],
	[MapToolMode.Measure, "m"],
	[MapToolMode.MapLayers, "l"],
	[MapToolMode.Locations, "o"],
	[MapToolMode.Filter, "F"],
	[MapToolMode.Accessibility, "c"]
]);

export const drawToolNames:Map<FeatureMode, string> = new Map<FeatureMode, string>([
	[FeatureMode.Polygon, "polygon"],
	[FeatureMode.Point, "point"],
	[FeatureMode.LineString, "linestring"],
	[FeatureMode.LinearRing, "cookiecutter"]
]);

/**
	* Get map tool mode description
	* 
	* @param mode			- 	Map tool mode to describe
	* @param showShortcut	-	Show the keyboard shortcut in description?
*/
export function getModeDescription(mode:MapToolMode, showShortcut:boolean = false):string {
	switch(mode) {
		case MapToolMode.Select:
			return getLocaleString("selecttool") + (showShortcut ? " (V)" : "");
			
		case MapToolMode.Draw:
			return getLocaleString("drawtool") + (showShortcut ? " (D)" : "");
			
		case MapToolMode.MapLayers:
			return getLocaleString("maplayerstool") + (showShortcut ? " (L)" : "");
			
		case MapToolMode.Address:
			return getLocaleString("addresstool") + (showShortcut ? " (A)" : "");
			
		case MapToolMode.Upload:
			return getLocaleString("uploadtool") + (showShortcut ? " (U)" : "");
			
		case MapToolMode.More:
			return getLocaleString("moretool");
			
		case MapToolMode.MapSelector:
			return getLocaleString("mapselectortool");
			
		case MapToolMode.Print:
			return getLocaleString("print");
			
		case MapToolMode.Locations:
			return getLocaleString("locations") + (showShortcut ? " (O)" : "");
			
		case MapToolMode.Measure:
			return getLocaleString("measure") + (showShortcut ? " (M)" : "");
			
		case MapToolMode.Filter:
			return getLocaleString("filter") + (showShortcut ? " (F)" : "");
		
		case MapToolMode.Tutorial:
			return getLocaleString("tutorial");
		
		case MapToolMode.Permalink:
			return getLocaleString("permalink");
		
		case MapToolMode.Annotation:
			return getLocaleString("annotation");
		
		case MapToolMode.MapControl:
			return getLocaleString("mapcontrol");
		
		case MapToolMode.LinkedResources:
			return getLocaleString("linkedresources");
		
		case MapToolMode.Accessibility:
			return getLocaleString("accessibility");
	}
	return "Unknown mode";
}

/**
	* Get tool mode override enabled
	* 
	* @param {MapToolMode} mode - Tool mode to check
*/
export function getToolEnabledOverride(mode:MapToolMode):boolean {
	return _config.toolsEnabledOverride?.indexOf(toolModeNames.get(mode)) > -1;
}

/**
	* Get tool mode override disabled
	* 
	* @param {MapToolMode} mode - Tool mode to check
*/
export function getToolDisabledOverride(mode:MapToolMode):boolean {
	return _config.toolsDisabledOverride?.indexOf(toolModeNames.get(mode)) > -1;
}

/**
	* Get feature mode override enabled
	* 
	* @param {FeatureMode} mode - Feature mode to check
*/
export function getDrawToolEnabled(mode:FeatureMode, mapConfig:any):boolean {
	if(_config.drawToolsMaster)
		return _config.drawToolsMaster.includes(mode);
	
	return getInitialDrawToolEnabled(mode, mapConfig);
}

/**
	* Get enabled draw tool / feature modes
	* 
	* @returns {FeatureMode[]} modes - Feature modes enabled
*/
export function getDrawToolsEnabled(mapConfig:any):FeatureMode[] {
	const modes:FeatureMode[] = [];
	
	if(_config.drawToolsMaster)
		return _config.drawToolsMaster;
	
	if(_config.drawToolsOverride && mapConfig.additionalConfig?.drawToolsImportant !== true) {
		_config.drawToolsOverride.forEach((id:string) => {
			const featureMode:FeatureMode = getIndexByValue(drawToolNames, id);
			
			!!featureMode && modes.push(featureMode);
		});
	}else{
		const configToolsEnabled:string[] = mapConfig.additionalConfig?.drawToolsEnabled;
		if(Array.isArray(configToolsEnabled)) {
			configToolsEnabled.forEach((id:string) => {
				const featureMode:FeatureMode = getIndexByValue(drawToolNames, id);
				
				!!featureMode && modes.push(featureMode);
			})
		}else{
			modes.push(...Array.from(drawToolNames.keys()));
		}
	}
	
	return modes;
}

/**
	* Get tool mode from name
	* 
	* @param {string} name - Tool mode name to convert
*/
export function getToolModeByName(name:string):MapToolMode {
	let toolMode:MapToolMode = null;
	
	toolModeNames.forEach((modeName:string, mode:MapToolMode) => {
		if(modeName == name) {
			toolMode = mode;
			return;
		}
	});
	
	return toolMode;
}

/**
	* Get tool mode name
	* 
	* @param {MapToolMode} map tool mode - Tool mode to convert
*/
export function getToolModeName(mapToolMode:MapToolMode):string {
	let name:string;
	
	toolModeNames.forEach((modeName:string, mode:MapToolMode) => {
		if(mapToolMode == mode) {
			name = modeName;
			return;
		}
	});
	
	return name;
}

/**
	* Get tool mode names
	* 
	* @param {MapToolMode[]} map tool modes - Tool modes to convert
*/
export function getToolModeNames(mapToolModes:MapToolMode[]):string[] {
	const names:string[] = [];
	
	toolModeNames.forEach((modeName:string, mode:MapToolMode) => {
		if(mapToolModes.includes(mode)) {
			names.push(modeName);
		}
	});
	
	return names;
}

/**
	* Get draw tool mode names
	* 
	* @param {FeatureMode[]} draw tool modes - Draw tool modes to convert
*/
export function getDrawToolModeNames(featureModes:FeatureMode[]):string[] {
	const names:string[] = [];
	
	drawToolNames.forEach((name:string, featureMode:FeatureMode) => {
		if(featureModes.includes(featureMode)) {
			names.push(name);
		}
	});
	
	return names;
}

/**
	* Is a drawing tool mode enabled?
	* 
	* @param {MapToolMode} mode - Tool mode to check
	* 
	* @returns {boolean} Is a drawing tool mode enabled
*/
export function getDrawingToolEnabled(mode:MapToolMode):boolean {
	return (
		mode == MapToolMode.Draw ||
		mode == MapToolMode.Measure ||
		mode == MapToolMode.Annotation
	);
}

/**
	* Get tool enabled
	* @param {MapToolMode} mode - Tool mode to check
	* @param {any} mapConfig - Map config to use (default = null)
	* 
	* @returns {boolean} Is this tool enabled?
*/
export function getToolEnabled(mode:MapToolMode, mapConfig:any):boolean {
	if(_config.toolsMaster)
		return _config.toolsMaster.includes(mode);
	
	switch(mode) {
		case MapToolMode.Select: {
			return !getToolDisabledOverride(MapToolMode.Select);
		}
		case MapToolMode.MapLayers: {
			return _config.showLayers && !getToolDisabledOverride(MapToolMode.MapLayers);
		}
		case MapToolMode.Draw: {
			return ( (mapConfig.drawingTools?.polygonEnabled && mapConfig.drawingTools?.pointEnabled) || getToolEnabledOverride(MapToolMode.Draw)) && !getToolDisabledOverride(MapToolMode.Draw);
		}
		case MapToolMode.Upload: {
			return (getToolEnabled(MapToolMode.Draw, mapConfig) || getToolEnabled(MapToolMode.Annotation, mapConfig) || getToolEnabledOverride(MapToolMode.Upload)) && !getToolDisabledOverride(MapToolMode.Upload);
		}
		case MapToolMode.Measure: {
			return (mapConfig.measuringTools?.areaEnabled || mapConfig.measuringTools?.distanceEnabled || getToolEnabledOverride(MapToolMode.Measure)) && !getToolDisabledOverride(MapToolMode.Measure);
		}
		case MapToolMode.Address: {
			return (mapConfig.addressSearch?.enabled || getToolEnabledOverride(MapToolMode.Address)) && !getToolDisabledOverride(MapToolMode.Address);
		}
		case MapToolMode.Locations: {
			return (mapConfig.locations || mapConfig.sites || getToolEnabledOverride(MapToolMode.Locations)) && !getToolDisabledOverride(MapToolMode.Locations);
		}
		case MapToolMode.Print: {
			return (mapConfig.printTool?.enabled || getToolEnabledOverride(MapToolMode.Print)) && !getToolDisabledOverride(MapToolMode.Print);
		}
		case MapToolMode.Filter: {
			return !getToolDisabledOverride(MapToolMode.Filter);
		}
		case MapToolMode.Tutorial: {
			return !getToolDisabledOverride(MapToolMode.Tutorial);
		}
		case MapToolMode.Permalink: {
			return !getToolDisabledOverride(MapToolMode.Permalink) || getToolEnabledOverride(MapToolMode.Permalink);
		}
		case MapToolMode.Annotation: {
			return (mapConfig.annotateEnabled || getToolEnabledOverride(MapToolMode.Annotation)) && !getToolDisabledOverride(MapToolMode.Annotation);
		}
		case MapToolMode.MapControl: {
			return !getToolDisabledOverride(MapToolMode.MapControl);
		}
		case MapToolMode.LinkedResources: {
			return (mapConfig.linkedResources?.length > 0 || getToolEnabledOverride(MapToolMode.LinkedResources)) && !getToolDisabledOverride(MapToolMode.LinkedResources);
		}
		case MapToolMode.Accessibility: {
			return !getToolDisabledOverride(MapToolMode.Accessibility);
		}
	}
	
	return false;
}

/**
	* Get if a tool is initially meant to be enabled
	* by the configurations set from the start
	* 
	* @param {MapToolMode} mode - Mode to check
	* 
	* @returns {boolean}
	* 
	* @remarks Requires global config
*/
export function getInitialToolEnabled(mode:MapToolMode) {
	return _config.initialToolsEnabled.get(mode);
}

/**
	* Get if a draw tool is initially meant to be enabled
	* by the configurations set from the start
	* 
	* @param {FeatureMode} mode - Mode to check
	* 
	* @returns {boolean}
	* 
	* @remarks Requires global and map config
*/
export function getInitialDrawToolEnabled(mode:FeatureMode, mapConfig:any) {
	if(_config.drawToolsOverride && mapConfig.additionalConfig?.drawToolsImportant !== true) {
		return _config.drawToolsOverride.includes(drawToolNames.get(mode));
	}else
	if(Array.isArray(mapConfig.additionalConfig?.drawToolsEnabled)) {
		return mapConfig.additionalConfig?.drawToolsEnabled.includes(drawToolNames.get(mode)) == true;
	}else{
		return true;
	}
}

/**
	* Get all tools enabled at any point
	* @returns {MapToolMode[]} tools enabled
*/
export function getToolsEnabled(mapConfig:any = null):MapToolMode[] {
	const toolsEnabled:MapToolMode[] = [];
	
	toolModeNames.forEach((modeName:string, mode:MapToolMode) => {
		getToolEnabled(mode, mapConfig) && toolsEnabled.push(mode);
	});
	
	return toolsEnabled;
}

/**
	* Set tools master (enabled tools master)
	* @param {string[]} ids - Tool IDs to enable
	* 
	* @remarks
	* This overrides the tools enabled/disabled overrides and only enables
	* these specific tools for the map as a master override
*/
export function setToolsMaster(ids:string[], updateHashed:boolean = false) {
	const toolsMaster:MapToolMode[] = [];
	ids?.forEach((id:string) => {
		const mapToolMode:MapToolMode = getIndexByValue(toolModeNames, id);
		
		// Check if this can be allowed based on initial config
		if(!getInitialToolEnabled(mapToolMode))
			return;
		
		toolsMaster.push(mapToolMode);
	});
	
	_config.toolsMaster = ids != null ? toolsMaster : null;
	
	if(updateHashed) {
		_events.invoke("hashed-update", {
			tools: ids
		});
	}
	
	_events.invoke("set-tools-master", _config.toolsMaster);
}

/**
	* Set draw tools master (enabled draw tools master)
	* @param {string[]} ids - Feature mode IDs to enable
	* 
	* @remarks
	* This overrides the feature modes enabled/disabled overrides and only enables
	* these specific feature mode tools for the map as a master override
*/
export function setDrawToolsMaster(ids:string[], updateHashed:boolean = false) {
	const toolsMaster:FeatureMode[] = [];
	ids?.forEach((id:string) => {
		const featureMode:FeatureMode = getIndexByValue(drawToolNames, id);
		
		// Check if this can be allowed based on initial config
		//if(!getInitialToolEnabled(mapToolMode))
			//return;
		
		toolsMaster.push(featureMode);
	});
	
	_config.drawToolsMaster = ids != null ? toolsMaster : null;
	
	if(updateHashed) {
		_events.invoke("hashed-update", {
			drawtools: ids
		});
	}
	
	_events.invoke("set-draw-tools-master", _config.drawToolsMaster);
}